"use strict";
/**
 * Canonical list of platform usage event types.
 *
 * This file is the single source of truth for the event taxonomy.
 * Both the frontend tracker (client/src/lib/analytics.ts) and the
 * server ingestion validator (services/api-service/src/routes/
 * analytics/events.ts) read from here, so client and server
 * cannot drift.
 *
 * Adding a new event type or event_name requires updating this file
 * AND the corresponding tracker call site / route validator. The
 * divergence test in __tests__/taxonomy.test.ts is the structural
 * guard.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SESSION_END_REASONS = exports.ARCHE_OPENED_SOURCES = exports.EVENT_NAMES_BY_TYPE = exports.EVENT_TYPES = void 0;
exports.isAllowedEventName = isAllowedEventName;
exports.EVENT_TYPES = [
    'page_view',
    'session_start',
    'session_heartbeat',
    'session_end',
    'feature_used',
    'workflow_started',
    'workflow_completed',
    'workflow_abandoned',
    'arche_opened',
    'arche_left',
    'chat_message_sent',
    'purchase_initiated',
    'purchase_flow_completed',
];
/**
 * Per-type allow-list of accepted `event_name` values.
 *
 * The server validator drops events whose `event_name` is not in the
 * allow-list for the given `event_type`. This prevents arbitrary
 * free-text from polluting the events table and enforces taxonomy
 * discipline (engineers extend the list deliberately, in a reviewed PR,
 * rather than emitting whatever string fits the moment).
 *
 * Naming conventions:
 *   - page_view: normalized page CATEGORIES, not full paths. The full
 *     URL goes in the event's `pagePath` column. Categories give us
 *     stable rollups even as the route table evolves.
 *   - feature_used: a fixed list of instrumented features. Adding a new
 *     feature event requires a PR that extends this list AND adds the
 *     call site.
 *   - workflow_*: workflow names paired across started/completed/abandoned.
 *   - purchase_*: SKU CATEGORIES for funnel analysis. Authoritative
 *     purchase records live in `payment_history`, not here.
 */
exports.EVENT_NAMES_BY_TYPE = {
    page_view: [
        'home',
        'auth',
        'arche_directory',
        'arche_view',
        'dev_console',
        'dev_console_tool',
        'profile',
        'settings',
        'plugin_builder',
        'submissions',
        'other',
    ],
    session_start: ['session_start'],
    session_heartbeat: ['session_heartbeat'],
    session_end: ['unload', 'idle', 'logout'],
    feature_used: [
        'arche_directory_search',
        'profile_edit_save',
        'plugin_builder_phase_advanced',
        'dev_console_tool_open',
        'arche_share',
        'theme_toggle',
    ],
    workflow_started: ['plugin_creation', 'arche_purchase', 'subscription_upgrade', 'onboarding'],
    workflow_completed: ['plugin_creation', 'arche_purchase', 'subscription_upgrade', 'onboarding'],
    workflow_abandoned: ['plugin_creation', 'arche_purchase', 'subscription_upgrade', 'onboarding'],
    arche_opened: ['arche_opened'],
    arche_left: ['arche_left'],
    chat_message_sent: ['chat_message_sent'],
    purchase_initiated: ['credits', 'subscription', 'arche'],
    purchase_flow_completed: ['credits', 'subscription', 'arche'],
};
/**
 * Source attribution for `arche_opened` events. Tells us how a user
 * arrived at the arche — discoverability data for the dashboard.
 */
exports.ARCHE_OPENED_SOURCES = ['directory', 'search', 'recommended', 'direct_link'];
/**
 * Reasons a session ended. Best-effort — sendBeacon is not guaranteed.
 */
exports.SESSION_END_REASONS = ['unload', 'idle', 'logout'];
/**
 * Returns true if (eventType, eventName) is in the canonical allow-list.
 * Used by both the server validator and (optionally) the client tracker.
 */
function isAllowedEventName(eventType, eventName) {
    return exports.EVENT_NAMES_BY_TYPE[eventType].includes(eventName);
}
//# sourceMappingURL=event-types.js.map